import { useEffect, useRef, useState } from 'react';

const dragHasFiles = (event: DragEvent) =>
  Array.from(event.dataTransfer?.types ?? []).includes('Files');

/**
 * 생성기 입력창이 열린 동안 화면 어디로든 파일을 끌어오면 놓기 화면을 켜고, 놓으면 첫 파일을 넘긴다.
 * 뒤 화면은 inert 라 React 핸들러가 닿지 않아 window 에서 듣는다. 파일이 아닌 끌기는 무시한다.
 */
export const useAiGenerateDockFileDrop = ({
  enabled,
  onDropFile,
}: {
  enabled: boolean;
  onDropFile: (file: File) => void;
}) => {
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const onDropFileRef = useRef(onDropFile);
  onDropFileRef.current = onDropFile;

  useEffect(() => {
    if (!enabled) return;

    const handleDragOver = (event: DragEvent) => {
      if (!dragHasFiles(event)) return;
      event.preventDefault();
      if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
      setIsDraggingFile(true);
    };
    // 창 밖으로 나갈 때만 relatedTarget 이 비어 있다 — 화면 안 요소 사이 이동은 무시한다.
    const handleDragLeave = (event: DragEvent) => {
      if (event.relatedTarget === null) setIsDraggingFile(false);
    };
    const handleDrop = (event: DragEvent) => {
      if (!dragHasFiles(event)) return;
      event.preventDefault();
      setIsDraggingFile(false);
      const file = event.dataTransfer?.files[0];
      if (file) onDropFileRef.current(file);
    };

    window.addEventListener('dragover', handleDragOver);
    window.addEventListener('dragleave', handleDragLeave);
    window.addEventListener('drop', handleDrop);
    return () => {
      window.removeEventListener('dragover', handleDragOver);
      window.removeEventListener('dragleave', handleDragLeave);
      window.removeEventListener('drop', handleDrop);
      // 끌던 중 꺼지면(닫기·Esc·숨김) dragleave 를 더는 못 들으니 여기서 끝낸다 — 다시 켤 때 놓기 화면이 남지 않게.
      setIsDraggingFile(false);
    };
  }, [enabled]);

  return { isDraggingFile: enabled && isDraggingFile };
};
